import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  createClientCreativeKnowledge,
  deleteClientCreativeKnowledge,
  deleteClientOffer,
  fetchClientCreativeKnowledge,
  fetchClientOffers,
  fetchSharedVerticalCreativeAssets,
  updateClientCreativeKnowledge,
} from '@src/api';
import { autoFillCreativeKnowledgeProofs } from '@src/api/actions/dashboard/clientCreativeKnowledge';
import { fetchVerticals } from '@src/api/actions/verticals';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { useAuth } from '@src/context';
import ClientOfferEditorDialog from './components/ClientOfferEditorDialog';

const EMPTY_SHARED_VERTICAL_ASSET = {
  id: undefined,
  vertical: '',
  general_asset_folder_url: '',
  recent_ad_examples_url: '',
};

const EMPTY_CLIENT_VERTICAL_ASSET = {
  id: undefined,
  vertical: '',
  client_asset_folder_url: '',
};

const BATH_MANUFACTURER_OPTIONS = [
  { value: 'bci', label: 'BCI' },
  { value: 'jacuzzi', label: 'Jacuzzi' },
  { value: 'kohler', label: 'Kohler' },
  { value: 'samuel_mueller', label: 'Samuel Mueller' },
];

const DIALOG_SECTIONS = [
  { key: 'basics', label: 'Basics' },
  { key: 'notes', label: 'Notes & AI' },
  { key: 'proofs', label: 'Proofs' },
  { key: 'verticals', label: 'Verticals & Offers' },
];

const EMPTY_RECORD = {
  client: '',
  bath_manufacturer: [],
  bath_manufacturer_label: '',
  bath_manufacturer_options: [],
  verticals: [],
  vertical_details: [],
  website_url: '',
  logo_url: '',
  notes: '',
  ai_script_instructions: '',
  proof_warranty: '',
  proof_experience: '',
  proof_certifications: '',
  proof_install_quality: '',
  proof_slots_filled_at: null,
  client_vertical_assets: [],
  shared_vertical_assets: [],
};

function collectErrorMessages(value, prefix = '') {
  if (!value) {
    return [];
  }
  if (typeof value === 'string') {
    return [prefix ? `${prefix}: ${value}` : value];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => collectErrorMessages(item, prefix || `Item ${index + 1}`));
  }
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nestedValue]) => {
      const nextPrefix = prefix ? `${prefix} > ${key}` : key;
      return collectErrorMessages(nestedValue, nextPrefix);
    });
  }
  return [prefix ? `${prefix}: ${String(value)}` : String(value)];
}

function getApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length) {
    return String(data.non_field_errors[0]);
  }
  if (data && typeof data === 'object') {
    const firstValue = Object.values(data).find((value) => (Array.isArray(value) ? value.length > 0 : Boolean(value)));
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    if (typeof firstValue === 'string' && firstValue.trim()) return firstValue;
  }
  return fallback;
}

function getApiFieldErrors(err) {
  const data = err?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {};
  }
  return data;
}

function normalizeUrl(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return '';
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

// Field checklist behind the per-record completeness chip. Only counts fields
// the flight/creative pipeline actually consumes and that this page can edit.
function computeCompleteness(row) {
  const details = Array.isArray(row.vertical_details) ? row.vertical_details : [];
  const proofsFilled = ['proof_warranty', 'proof_experience', 'proof_certifications', 'proof_install_quality']
    .filter((field) => String(row[field] || '').trim()).length;
  const items = [
    { label: 'Website URL', done: Boolean(row.website_url) },
    { label: 'Logo URL', done: Boolean(row.logo_url) },
    { label: 'Notes', done: Boolean(String(row.notes || '').trim()) },
    { label: 'AI Script Instructions', done: Boolean(String(row.ai_script_instructions || '').trim()) },
    { label: `Proof slots (${proofsFilled}/4)`, done: proofsFilled === 4 },
    {
      label: 'Offer on every vertical',
      done: details.length > 0 && details.every((detail) => (detail.offers || []).length > 0),
    },
    {
      label: 'Client asset folder on every vertical',
      done: details.length > 0 && details.every((detail) => Boolean(detail.client_asset_folder_url)),
    },
  ];
  return { items, done: items.filter((item) => item.done).length, total: items.length };
}

function ExternalLink({ href, label }) {
  if (!href) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      size="small"
      startIcon={<LinkIcon fontSize="small" />}
      sx={{ px: 0.25, minWidth: 0, justifyContent: 'flex-start', lineHeight: 1.1 }}
    >
      {label}
    </Button>
  );
}

export default function ClientCreativeKnowledgePage() {
  const { state: authState } = useAuth();
  const authUser = authState?.user;
  const isStaff = authUser?.is_staff || authUser?.is_superuser;

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialClientId = String(location?.state?.clientId ?? searchParams.get('client_id') ?? '');

  const [rows, setRows] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [clientOffers, setClientOffers] = useState([]);
  const [filterClient, setFilterClient] = useState(initialClientId);
  const [filterVertical, setFilterVertical] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoFillError, setAutoFillError] = useState('');
  const [loadingSharedAssets, setLoadingSharedAssets] = useState(false);
  const [current, setCurrent] = useState(EMPTY_RECORD);
  const [offerEditor, setOfferEditor] = useState({
    open: false,
    offer: null,
    clientId: '',
    clientLabel: '',
    verticalOptions: [],
    defaultVertical: '',
    source: 'dialog',
  });
  const [offersDirty, setOffersDirty] = useState(false);
  const [offerError, setOfferError] = useState('');
  const [sortMode, setSortMode] = useState('');
  const sectionRefs = useRef({});

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
    addOption: addClientOption,
  } = useClientAutocomplete();

  const loadVerticals = async () => {
    try {
      const res = await fetchVerticals();
      setVerticals(Array.isArray(res.data) ? res.data : []);
    } catch (_err) {
      // Keep page usable if verticals fail.
    }
  };

  const loadRows = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchClientCreativeKnowledge({
        client_id: filterClient || undefined,
        vertical_id: filterVertical || undefined,
        search: search || undefined,
      });
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
          ? res.data.results
          : [];
      data.forEach((row) => {
        if (row.client && row.client_name) {
          addClientOption({ value: row.client, label: row.client_name });
        }
      });
      setRows(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load creative knowledge'));
    } finally {
      setLoading(false);
    }
  };

  const loadClientOffers = async (clientId) => {
    if (!clientId) {
      setClientOffers([]);
      return;
    }
    try {
      const res = await fetchClientOffers({ client_id: clientId });
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
          ? res.data.results
          : [];
      setClientOffers(data);
    } catch (_err) {
      setClientOffers([]);
    }
  };

  useEffect(() => {
    loadVerticals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-apply filters — covers the initial load too. The debounce is for
  // typing in the search box; client/vertical changes just ride the same timer.
  useEffect(() => {
    const timer = setTimeout(loadRows, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterClient, filterVertical, search]);

  useEffect(() => {
    const currentClientId = searchParams.get('client_id') || '';
    const nextClientId = filterClient ? String(filterClient) : '';
    if (currentClientId === nextClientId) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    if (nextClientId) {
      nextParams.set('client_id', nextClientId);
    } else {
      nextParams.delete('client_id');
    }
    setSearchParams(nextParams, { replace: true });
  }, [filterClient, searchParams, setSearchParams]);

  useEffect(() => {
    loadClientOffers(current.client);
  }, [current.client]);

  useEffect(() => {
    let cancelled = false;

    const syncSharedAssets = async () => {
      const verticalIds = current.verticals || [];
      if (!verticalIds.length) {
        setCurrent((prev) => ({
          ...prev,
          client_vertical_assets: [],
          shared_vertical_assets: [],
        }));
        return;
      }

      setLoadingSharedAssets(true);
      try {
        const res = await fetchSharedVerticalCreativeAssets(verticalIds);
        if (cancelled) {
          return;
        }
        const fetchedRows = Array.isArray(res.data) ? res.data : [];
        const fetchedByVertical = new Map(
          fetchedRows.map((row) => [String(row.vertical), row])
        );

        setCurrent((prev) => {
          const selectedVerticalIds = new Set(verticalIds.map((value) => String(value)));
          const existingClientRows = prev.client_vertical_assets.filter(
            (row) => selectedVerticalIds.has(String(row.vertical))
          );
          const existingClientByVertical = new Map(
            existingClientRows.map((row) => [String(row.vertical), row])
          );
          const existingRows = prev.shared_vertical_assets.filter((row) => selectedVerticalIds.has(String(row.vertical)));
          const existingByVertical = new Map(
            existingRows.map((row) => [String(row.vertical), row])
          );
          const nextClientRows = verticalIds.map((verticalId) => {
            const key = String(verticalId);
            const existing = existingClientByVertical.get(key);
            if (existing) {
              return existing;
            }
            const detail = Array.isArray(prev.vertical_details)
              ? prev.vertical_details.find((item) => String(item.vertical) === key)
              : null;
            if (detail?.client_asset_folder_url) {
              return {
                ...EMPTY_CLIENT_VERTICAL_ASSET,
                vertical: verticalId,
                client_asset_folder_url: detail.client_asset_folder_url,
              };
            }
            return {
              ...EMPTY_CLIENT_VERTICAL_ASSET,
              vertical: verticalId,
            };
          });

          const nextRows = verticalIds.map((verticalId) => {
            const key = String(verticalId);
            const existing = existingByVertical.get(key);
            const fetched = fetchedByVertical.get(key);
            const existingHasValues = Boolean(
              existing?.id
                || existing?.general_asset_folder_url
                || existing?.recent_ad_examples_url
            );

            if (existingHasValues) {
              return existing;
            }

            if (fetched) {
              return {
                id: fetched.id,
                vertical: fetched.vertical,
                general_asset_folder_url: fetched.general_asset_folder_url || '',
                recent_ad_examples_url: fetched.recent_ad_examples_url || '',
              };
            }

            return {
              ...EMPTY_SHARED_VERTICAL_ASSET,
              vertical: verticalId,
            };
          });

          return {
            ...prev,
            client_vertical_assets: nextClientRows,
            shared_vertical_assets: nextRows,
          };
        });
      } catch (_err) {
        if (cancelled) {
          return;
        }
      } finally {
        if (!cancelled) {
          setLoadingSharedAssets(false);
        }
      }
    };

    syncSharedAssets();

    return () => {
      cancelled = true;
    };
  }, [current.verticals]);

  // Save first if dirty (so the latest notes + ai_script_instructions land on
  // the row), then run the Claude extraction, then re-load the row state with
  // the freshly-filled proof slots so they show up in the form.
  const handleAutoFillProofs = async () => {
    if (!current.id) {
      setAutoFillError('Save the record first before auto-filling.');
      return;
    }
    setAutoFilling(true);
    setAutoFillError('');
    try {
      const res = await autoFillCreativeKnowledgeProofs(current.id);
      const updated = res?.data || res;
      setCurrent((prev) => ({
        ...prev,
        proof_warranty: updated.proof_warranty || '',
        proof_experience: updated.proof_experience || '',
        proof_certifications: updated.proof_certifications || '',
        proof_install_quality: updated.proof_install_quality || '',
        proof_slots_filled_at: updated.proof_slots_filled_at || null,
      }));
    } catch (err) {
      setAutoFillError(getApiErrorMessage(err, 'Auto-fill failed.'));
    } finally {
      setAutoFilling(false);
    }
  };

  const handleOpen = (row = EMPTY_RECORD) => {
    setClientOffers([]);
    const nextClient = row.client || '';
    setCurrent({
      ...EMPTY_RECORD,
      ...row,
      client: nextClient,
      bath_manufacturer: Array.isArray(row.bath_manufacturer) ? row.bath_manufacturer : [],
      bath_manufacturer_label: row.bath_manufacturer_label || '',
      bath_manufacturer_options: Array.isArray(row.bath_manufacturer_options)
        ? row.bath_manufacturer_options
        : [],
      verticals: Array.isArray(row.verticals) ? row.verticals : [],
      client_vertical_assets: Array.isArray(row.client_vertical_assets)
        ? row.client_vertical_assets.map((item) => ({
          id: item.id,
          vertical: item.vertical || '',
          client_asset_folder_url: item.client_asset_folder_url || '',
        }))
        : [],
      shared_vertical_assets: Array.isArray(row.shared_vertical_assets)
        ? row.shared_vertical_assets.map((item) => ({
          id: item.id,
          vertical: item.vertical || '',
          general_asset_folder_url: item.general_asset_folder_url || '',
          recent_ad_examples_url: item.recent_ad_examples_url || '',
        }))
        : [],
    });
    setFieldErrors({});
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setCurrent(EMPTY_RECORD);
    setFieldErrors({});
    setOfferError('');
    if (offersDirty) {
      setOffersDirty(false);
      loadRows();
    }
  };

  const openOfferEditor = (offer = null, verticalId = '') => {
    setOfferEditor({
      open: true,
      offer,
      clientId: current.client,
      clientLabel: findClientOptionByValue(current.client)?.label || '',
      verticalOptions: verticals.filter((vertical) => current.verticals.includes(vertical.id)),
      defaultVertical: verticalId || '',
      source: 'dialog',
    });
  };

  const openOfferEditorForRow = (row, detail) => {
    setOfferEditor({
      open: true,
      offer: null,
      clientId: row.client,
      clientLabel: row.client_name || findClientOptionByValue(row.client)?.label || '',
      verticalOptions: verticals.filter((vertical) => (row.verticals || []).includes(vertical.id)),
      defaultVertical: detail?.vertical || '',
      source: 'list',
    });
  };

  const closeOfferEditor = () => setOfferEditor((prev) => ({ ...prev, open: false }));

  const scrollToSection = (key) => {
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOfferSaved = async () => {
    if (offerEditor.source === 'list') {
      await loadRows();
      return;
    }
    setOffersDirty(true);
    setOfferError('');
    await loadClientOffers(current.client);
  };

  const handleOfferDelete = async (offerId) => {
    if (!window.confirm('Delete this offer?')) return;
    setOfferError('');
    try {
      await deleteClientOffer(offerId);
      setOffersDirty(true);
      await loadClientOffers(current.client);
    } catch (err) {
      setOfferError(getApiErrorMessage(err, 'Unable to delete offer'));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setFieldErrors({});
    const payload = {
      client: current.client,
      bath_manufacturer: current.bath_manufacturer || [],
      verticals: current.verticals,
      website_url: current.website_url,
      logo_url: current.logo_url,
      notes: current.notes,
      ai_script_instructions: current.ai_script_instructions || '',
      proof_warranty: current.proof_warranty || '',
      proof_experience: current.proof_experience || '',
      proof_certifications: current.proof_certifications || '',
      proof_install_quality: current.proof_install_quality || '',
      client_vertical_assets: current.client_vertical_assets
        .filter((item) => item.vertical)
        .map((item) => ({
          ...(item.id ? { id: item.id } : {}),
          vertical: item.vertical,
          client_asset_folder_url: item.client_asset_folder_url || '',
        })),
      shared_vertical_assets: current.shared_vertical_assets
        .filter((item) => item.vertical)
        .map((item) => ({
          ...(item.id ? { id: item.id } : {}),
          vertical: item.vertical,
          general_asset_folder_url: item.general_asset_folder_url || '',
          recent_ad_examples_url: item.recent_ad_examples_url || '',
        })),
    };
    try {
      if (current.id) {
        await updateClientCreativeKnowledge(current.id, payload);
      } else {
        await createClientCreativeKnowledge(payload);
      }
      handleClose();
      await loadRows();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to save creative knowledge'));
      setFieldErrors(getApiFieldErrors(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this creative knowledge record?')) return;
    setError('');
    try {
      await deleteClientCreativeKnowledge(id);
      await loadRows();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to delete creative knowledge'));
    }
  };

  const updateVerticalAssetRow = (index, field, value) => {
    setCurrent((prev) => ({
      ...prev,
      shared_vertical_assets: prev.shared_vertical_assets.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      )),
    }));
  };
  const updateClientVerticalAssetRow = (index, value) => {
    setCurrent((prev) => ({
      ...prev,
      client_vertical_assets: prev.client_vertical_assets.map((row, rowIndex) => (
        rowIndex === index ? { ...row, client_asset_folder_url: value } : row
      )),
    }));
  };

  const errorList = collectErrorMessages(fieldErrors);
  const clientVerticalAssetErrors = Array.isArray(fieldErrors.client_vertical_assets)
    ? fieldErrors.client_vertical_assets
    : [];
  const sharedVerticalAssetErrors = Array.isArray(fieldErrors.shared_vertical_assets)
    ? fieldErrors.shared_vertical_assets
    : [];
  const clientError = Array.isArray(fieldErrors.client) ? fieldErrors.client[0] : '';
  const verticalsError = Array.isArray(fieldErrors.verticals) ? fieldErrors.verticals[0] : '';
  const websiteError = Array.isArray(fieldErrors.website_url) ? fieldErrors.website_url[0] : '';
  const logoError = Array.isArray(fieldErrors.logo_url) ? fieldErrors.logo_url[0] : '';
  const notesError = Array.isArray(fieldErrors.notes) ? fieldErrors.notes[0] : '';
  const currentVerticalDetails = Array.isArray(current.vertical_details) ? current.vertical_details : [];
  const verticalDetailsById = new Map(currentVerticalDetails.map((detail) => [String(detail.vertical), detail]));
  const bathManufacturerError = Array.isArray(fieldErrors.bath_manufacturer) ? fieldErrors.bath_manufacturer[0] : '';
  const hasBathVertical = current.verticals.some((verticalId) => (
    verticals.find((item) => item.id === verticalId)?.vertical === 'Bath'
  ));
  const bathManufacturerOptions = Array.isArray(current.bath_manufacturer_options)
    && current.bath_manufacturer_options.length
    ? current.bath_manufacturer_options
    : BATH_MANUFACTURER_OPTIONS;
  const formatOfferLabel = (offer) => {
    const baseLabel = offer.display_label || offer.label || offer.name || `Offer ${offer.id}`;
    if (offer.offer_type === 'financing') return `Financing: ${baseLabel}`;
    if (offer.offer_type === 'lp_offer') return `LP: ${baseLabel}`;
    return `Ad: ${baseLabel}`;
  };
  const todayISO = new Date().toISOString().slice(0, 10);
  const isOfferExpired = (offer) => Boolean(offer.expires_at && offer.expires_at < todayISO);
  const derivedOffersByVertical = new Map();
  const unassignedOffers = [];
  clientOffers.forEach((offer) => {
    const verticalId = String(offer.vertical || '');
    if (!verticalId) {
      unassignedOffers.push(offer);
      return;
    }
    if (!derivedOffersByVertical.has(verticalId)) {
      derivedOffersByVertical.set(verticalId, []);
    }
    derivedOffersByVertical.get(verticalId).push(offer);
  });
  const displayRows = useMemo(() => {
    if (!sortMode) return rows;
    const direction = sortMode === 'most_complete' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const diff = (computeCompleteness(a).done - computeCompleteness(b).done) * direction;
      if (diff !== 0) return diff;
      return String(a.client_name || '').localeCompare(String(b.client_name || ''));
    });
  }, [rows, sortMode]);
  const handleUrlBlur = (field) => (event) => {
    const nextValue = normalizeUrl(event.target.value);
    setCurrent((prev) => ({ ...prev, [field]: nextValue }));
  };
  const handleSharedAssetUrlBlur = (index, field) => (event) => {
    updateVerticalAssetRow(index, field, normalizeUrl(event.target.value));
  };
  const handleClientAssetUrlBlur = (index) => (event) => {
    updateClientVerticalAssetRow(index, normalizeUrl(event.target.value));
  };

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5" fontWeight={700}>
          Client Creative Knowledge
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows} disabled={loading}>
            Refresh
          </Button>
          {isStaff ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              Add Record
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={clientOptions}
                loading={clientOptionsLoading}
                value={findClientOptionByValue(filterClient) || null}
                onInputChange={(_event, value) => handleClientSearch(value)}
                onChange={(_event, value) => setFilterClient(value?.value || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Client" placeholder="All clients" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Vertical"
                value={filterVertical}
                onChange={(event) => setFilterVertical(event.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {verticals.map((vertical) => (
                  <MenuItem key={vertical.id} value={vertical.id}>
                    {vertical.vertical}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Client, notes, link, offer"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Sort"
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value)}
              >
                <MenuItem value="">Default</MenuItem>
                <MenuItem value="least_complete">Least complete first</MenuItem>
                <MenuItem value="most_complete">Most complete first</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : rows.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
              <Typography variant="h6" color="text.secondary">No creative knowledge records yet</Typography>
              <Typography variant="body2" color="text.secondary">{isStaff ? 'Add a record to store brand context, asset links, and AI script instructions for each client.' : 'No records available yet.'}</Typography>
              {isStaff ? <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>Add Record</Button> : null}
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {displayRows.map((row) => (
            <Card key={row.id} variant="outlined">
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Stack spacing={1.5}>
                  {/* Header row */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box>
                        <Typography fontWeight={700} variant="body1">{row.client_name || row.client}</Typography>
                        {row.parent_company_name ? <Typography variant="caption" color="text.secondary">{row.parent_company_name}</Typography> : null}
                      </Box>
                      {(() => {
                        const completeness = computeCompleteness(row);
                        const missing = completeness.items.filter((item) => !item.done).map((item) => item.label);
                        const complete = completeness.done === completeness.total;
                        return (
                          <Tooltip title={complete ? 'All fields filled' : `Missing: ${missing.join(', ')}`}>
                            <Chip
                              label={`${completeness.done}/${completeness.total}`}
                              size="small"
                              color={complete ? 'success' : completeness.done <= 3 ? 'error' : 'warning'}
                              variant="outlined"
                            />
                          </Tooltip>
                        );
                      })()}
                      {(row.vertical_details || []).map((d) => (
                        <Chip key={`v-${row.id}-${d.vertical}`} label={d.vertical_name} size="small" variant="outlined" />
                      ))}
                      {row.bath_manufacturer_label ? <Chip label={`Mfr: ${row.bath_manufacturer_label}`} size="small" color="info" variant="outlined" /> : null}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <ExternalLink href={row.website_url} label="Website" />
                      <ExternalLink href={row.logo_url} label="Logo" />
                      {isStaff ? (
                        <>
                          <IconButton size="small" onClick={() => handleOpen(row)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </>
                      ) : null}
                    </Stack>
                  </Stack>

                  {/* Notes preview */}
                  {row.notes ? (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.notes}
                    </Typography>
                  ) : null}

                  {/* AI Script Instructions + proof slot indicators */}
                  {(() => {
                    const filled = ['proof_warranty', 'proof_experience', 'proof_certifications', 'proof_install_quality']
                      .filter((f) => (row[f] || '').trim()).length;
                    return (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {row.ai_script_instructions ? (
                          <Chip label="AI Script Instructions configured" size="small" color="success" variant="outlined" />
                        ) : null}
                        {row.proof_slots_filled_at || filled > 0 ? (
                          <Chip
                            label={`Proofs: ${filled}/4`}
                            size="small"
                            color={filled === 4 ? 'success' : filled === 0 ? 'default' : 'warning'}
                            variant="outlined"
                            title={row.proof_slots_filled_at
                              ? `Auto-filled ${new Date(row.proof_slots_filled_at).toLocaleString()}`
                              : 'Manually filled'}
                          />
                        ) : null}
                      </Stack>
                    );
                  })()}

                  {/* Vertical details grid */}
                  {(row.vertical_details || []).length ? (
                    <Grid container spacing={1.5}>
                      {row.vertical_details.map((detail) => (
                        <Grid item xs={12} md={6} lg={4} key={`${row.id}-vd-${detail.vertical}`}>
                          <Card variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: 'action.hover' }}>
                            <Stack spacing={0.75}>
                              <Typography variant="subtitle2">{detail.vertical_name}</Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                                {(detail.offers || []).length
                                  ? detail.offers.map((offer) => (
                                    <Chip
                                      key={`${row.id}-o-${offer.id}`}
                                      size="small"
                                      label={isOfferExpired(offer) ? `${formatOfferLabel(offer)} (expired)` : formatOfferLabel(offer)}
                                      color={isOfferExpired(offer) ? 'error' : 'default'}
                                      variant={isOfferExpired(offer) ? 'outlined' : 'filled'}
                                      sx={{ height: 20, '& .MuiChip-label': { px: 0.5 } }}
                                    />
                                  ))
                                  : <Typography variant="caption" color="text.secondary">No offers</Typography>}
                                {isStaff ? (
                                  <Tooltip title="Add offer">
                                    <IconButton
                                      size="small"
                                      sx={{ p: 0.25 }}
                                      onClick={() => openOfferEditorForRow(row, detail)}
                                      aria-label="Add offer"
                                    >
                                      <AddIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              </Stack>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                <ExternalLink href={detail.client_asset_folder_url} label="Client Assets" />
                                <ExternalLink href={detail.shared_assets?.general_asset_folder_url} label="Shared Assets" />
                                <ExternalLink href={detail.shared_assets?.recent_ad_examples_url} label="Recent Ads" />
                              </Stack>
                            </Stack>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{current.id ? 'Edit Creative Knowledge' : 'Add Creative Knowledge'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {errorList.length > 0 ? (
              <Alert severity="error">
                <Stack spacing={0.5}>
                  {errorList.map((message, index) => (
                    <Typography key={`${message}-${index}`} variant="body2">
                      {message}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            ) : null}

            <Box
              sx={{
                position: 'sticky',
                top: -20,
                zIndex: 2,
                bgcolor: 'background.paper',
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1}>
                {DIALOG_SECTIONS.map((section) => (
                  <Chip
                    key={section.key}
                    label={section.label}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => scrollToSection(section.key)}
                  />
                ))}
              </Stack>
            </Box>

            <Stack
              spacing={2.5}
              ref={(el) => { sectionRefs.current.basics = el; }}
              sx={{ scrollMarginTop: '56px' }}
            >
            <Autocomplete
              options={clientOptions}
              loading={clientOptionsLoading}
              value={findClientOptionByValue(current.client) || null}
              onInputChange={(_event, value) => handleClientSearch(value)}
              onChange={(_event, value) => setCurrent((prev) => ({
                ...prev,
                client: value?.value || '',
                bath_manufacturer: [],
                bath_manufacturer_label: '',
                bath_manufacturer_options: prev.bath_manufacturer_options,
                verticals: [],
                vertical_details: [],
                client_vertical_assets: [],
                shared_vertical_assets: [],
              }))}
              renderInput={(params) => (
                <TextField {...params} label="Client" required error={Boolean(clientError)} helperText={clientError || ''} />
              )}
            />

            <Autocomplete
              multiple
              options={verticals}
              getOptionLabel={(option) => option.vertical || ''}
              value={verticals.filter((vertical) => current.verticals.includes(vertical.id))}
              onChange={(_event, value) => setCurrent((prev) => ({
                ...prev,
                verticals: value.map((item) => item.id),
              }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Verticals"
                  placeholder="Select client verticals"
                  error={Boolean(verticalsError)}
                  helperText={verticalsError || ''}
                />
              )}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Website URL"
                  type="url"
                  fullWidth
                  value={current.website_url}
                  onChange={(event) => setCurrent((prev) => ({ ...prev, website_url: event.target.value }))}
                  onBlur={handleUrlBlur('website_url')}
                  error={Boolean(websiteError)}
                  helperText={websiteError || ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Logo URL"
                  type="url"
                  fullWidth
                  value={current.logo_url}
                  onChange={(event) => setCurrent((prev) => ({ ...prev, logo_url: event.target.value }))}
                  onBlur={handleUrlBlur('logo_url')}
                  error={Boolean(logoError)}
                  helperText={logoError || ''}
                />
              </Grid>
            </Grid>

            {hasBathVertical ? (
              <Autocomplete
                multiple
                options={bathManufacturerOptions}
                getOptionLabel={(option) => option.label || ''}
                value={bathManufacturerOptions.filter((option) => current.bath_manufacturer.includes(option.value))}
                onChange={(_event, value) => setCurrent((prev) => ({
                  ...prev,
                  bath_manufacturer: value.map((item) => item.value),
                }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bath Manufacturers"
                    error={Boolean(bathManufacturerError)}
                    helperText={bathManufacturerError || 'Stored on the client record for Bath vertical planning.'}
                  />
                )}
              />
            ) : null}
            </Stack>

            <Stack
              spacing={2.5}
              ref={(el) => { sectionRefs.current.notes = el; }}
              sx={{ scrollMarginTop: '56px' }}
            >
            <TextField
              label="Notes"
              multiline
              minRows={4}
              value={current.notes}
              onChange={(event) => setCurrent((prev) => ({ ...prev, notes: event.target.value }))}
              placeholder="Internal creative notes, positioning, visual constraints, brand direction, territory nuances..."
              error={Boolean(notesError)}
              helperText={notesError || ''}
            />

            <TextField
              label="AI Script Instructions"
              multiline
              minRows={3}
              value={current.ai_script_instructions || ''}
              onChange={(event) => setCurrent((prev) => ({ ...prev, ai_script_instructions: event.target.value }))}
              placeholder="Client-specific rules for the AI script generator (e.g. 'Always mention 25-year warranty', 'Premium luxury tone', 'Never reference competitor brands')..."
              helperText="These instructions are automatically included when generating ad scripts for this client."
            />
            </Stack>

            <Card
              variant="outlined"
              ref={(el) => { sectionRefs.current.proofs = el; }}
              sx={{ scrollMarginTop: '56px' }}
            >
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                      Proof slots
                    </Typography>
                    {current.proof_slots_filled_at ? (
                      <Typography variant="caption" color="text.secondary">
                        Auto-filled {new Date(current.proof_slots_filled_at).toLocaleString()}
                      </Typography>
                    ) : null}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={autoFilling ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                      onClick={handleAutoFillProofs}
                      disabled={autoFilling || !current.id || (!current.notes && !current.ai_script_instructions)}
                    >
                      {autoFilling ? 'Extracting…' : 'Auto-fill from KB'}
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Short per-client claims dropped into briefs via the&nbsp;
                    <code>{'{{client.warranty}}'}</code>, <code>{'{{client.experience}}'}</code>,&nbsp;
                    <code>{'{{client.certifications}}'}</code>, and&nbsp;
                    <code>{'{{client.install_quality}}'}</code> tokens. Auto-fill reads the
                    Notes + AI Script Instructions above and writes these once (~$0.005). Edit anything that's off.
                    Leave blank for a client to make that slot collapse silently in their scripts.
                  </Typography>
                  {autoFillError ? <Alert severity="error">{autoFillError}</Alert> : null}
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth size="small"
                        label="Warranty"
                        value={current.proof_warranty || ''}
                        onChange={(e) => setCurrent((prev) => ({ ...prev, proof_warranty: e.target.value }))}
                        placeholder="e.g. limited lifetime transferable warranty"
                        inputProps={{ maxLength: 160 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth size="small"
                        label="Experience / years in business"
                        value={current.proof_experience || ''}
                        onChange={(e) => setCurrent((prev) => ({ ...prev, proof_experience: e.target.value }))}
                        placeholder="e.g. family-owned since 1986"
                        inputProps={{ maxLength: 160 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth size="small"
                        label="Certifications"
                        value={current.proof_certifications || ''}
                        onChange={(e) => setCurrent((prev) => ({ ...prev, proof_certifications: e.target.value }))}
                        placeholder="e.g. GAF Master Elite-certified installer"
                        inputProps={{ maxLength: 160 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth size="small"
                        label="Install quality / approach"
                        value={current.proof_install_quality || ''}
                        onChange={(e) => setCurrent((prev) => ({ ...prev, proof_install_quality: e.target.value }))}
                        placeholder="e.g. factory-trained in-house crews"
                        inputProps={{ maxLength: 160 }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>

            <Divider />

            <Typography
              variant="subtitle1"
              fontWeight={700}
              ref={(el) => { sectionRefs.current.verticals = el; }}
              sx={{ scrollMarginTop: '56px' }}
            >
              Vertical Details
            </Typography>

            {offerError ? <Alert severity="error">{offerError}</Alert> : null}

            {current.verticals.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Select one or more client verticals to see offers and edit client-specific or shared asset links for each vertical.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {current.verticals.map((verticalId, index) => {
                  const detail = verticalDetailsById.get(String(verticalId));
                  const verticalLabel = verticals.find((item) => item.id === verticalId)?.vertical || detail?.vertical_name || verticalId;
                  const offers = derivedOffersByVertical.get(String(verticalId))
                    || (Array.isArray(detail?.offers) ? detail.offers : []);
                  const clientAssetRow = current.client_vertical_assets.find(
                    (r) => String(r.vertical) === String(verticalId),
                  ) || EMPTY_CLIENT_VERTICAL_ASSET;
                  const clientAssetIndex = current.client_vertical_assets.indexOf(clientAssetRow);
                  const sharedAssetRow = current.shared_vertical_assets.find(
                    (r) => String(r.vertical) === String(verticalId),
                  ) || EMPTY_SHARED_VERTICAL_ASSET;
                  const sharedAssetIndex = current.shared_vertical_assets.indexOf(sharedAssetRow);
                  const clientAssetRowErrors = clientVerticalAssetErrors[index] || {};
                  const clientAssetError = Array.isArray(clientAssetRowErrors.client_asset_folder_url)
                    ? clientAssetRowErrors.client_asset_folder_url[0]
                    : '';
                  const sharedAssetRowErrors = sharedVerticalAssetErrors[index] || {};
                  const generalAssetError = Array.isArray(sharedAssetRowErrors.general_asset_folder_url)
                    ? sharedAssetRowErrors.general_asset_folder_url[0]
                    : '';
                  const recentAdsError = Array.isArray(sharedAssetRowErrors.recent_ad_examples_url)
                    ? sharedAssetRowErrors.recent_ad_examples_url[0]
                    : '';
                  return (
                    <Card key={`derived-offers-${verticalId}`} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Typography variant="subtitle2">{verticalLabel}</Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                          {offers.length ? offers.map((offer) => (
                            <Chip
                              key={`derived-offer-${verticalId}-${offer.id}`}
                              size="small"
                              label={isOfferExpired(offer) ? `${formatOfferLabel(offer)} (expired)` : formatOfferLabel(offer)}
                              color={isOfferExpired(offer) ? 'error' : 'default'}
                              variant="outlined"
                              onClick={isStaff ? () => openOfferEditor(offer, verticalId) : undefined}
                              onDelete={isStaff ? () => handleOfferDelete(offer.id) : undefined}
                            />
                          )) : (
                            <Typography variant="body2" color="text.secondary">
                              No client offers found for this vertical.
                            </Typography>
                          )}
                          {isStaff ? (
                            <Button
                              size="small"
                              startIcon={<AddIcon fontSize="small" />}
                              onClick={() => openOfferEditor(null, verticalId)}
                              disabled={!current.client}
                            >
                              Add Offer
                            </Button>
                          ) : null}
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Client Asset Folder URL"
                              type="url"
                              value={clientAssetRow.client_asset_folder_url}
                              onChange={(event) => updateClientVerticalAssetRow(clientAssetIndex, event.target.value)}
                              onBlur={handleClientAssetUrlBlur(clientAssetIndex)}
                              error={Boolean(clientAssetError)}
                              helperText={clientAssetError || ''}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="General Asset Folder URL"
                              type="url"
                              value={sharedAssetRow.general_asset_folder_url}
                              onChange={(event) => updateVerticalAssetRow(sharedAssetIndex, 'general_asset_folder_url', event.target.value)}
                              onBlur={handleSharedAssetUrlBlur(sharedAssetIndex, 'general_asset_folder_url')}
                              error={Boolean(generalAssetError)}
                              helperText={generalAssetError || 'Shared across all clients in this vertical.'}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Recent Ad Examples URL"
                              type="url"
                              value={sharedAssetRow.recent_ad_examples_url}
                              onChange={(event) => updateVerticalAssetRow(sharedAssetIndex, 'recent_ad_examples_url', event.target.value)}
                              onBlur={handleSharedAssetUrlBlur(sharedAssetIndex, 'recent_ad_examples_url')}
                              error={Boolean(recentAdsError)}
                              helperText={recentAdsError || ''}
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            )}
            {unassignedOffers.length ? (
              <Card variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Offers without a vertical</Typography>
                  <Typography variant="caption" color="text.secondary">
                    These offers exist for this client but are not tied to a vertical, so they will
                    not appear on the vertical cards above. Click one to assign it.
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {unassignedOffers.map((offer) => (
                      <Chip
                        key={`unassigned-offer-${offer.id}`}
                        size="small"
                        label={isOfferExpired(offer) ? `${formatOfferLabel(offer)} (expired)` : formatOfferLabel(offer)}
                        color={isOfferExpired(offer) ? 'error' : 'default'}
                        variant="outlined"
                        onClick={isStaff ? () => openOfferEditor(offer) : undefined}
                        onDelete={isStaff ? () => handleOfferDelete(offer.id) : undefined}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Card>
            ) : null}
            {loadingSharedAssets ? (
              <Typography variant="body2" color="text.secondary">
                Loading shared assets for selected verticals...
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !current.client}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ClientOfferEditorDialog
        open={offerEditor.open}
        onClose={closeOfferEditor}
        onSaved={handleOfferSaved}
        clientId={offerEditor.clientId}
        clientLabel={offerEditor.clientLabel}
        offer={offerEditor.offer}
        verticalOptions={offerEditor.verticalOptions}
        defaultVertical={offerEditor.defaultVertical}
      />
    </Box>
  );
}
